import cn from "classnames"
import React, { useCallback, useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"

import styles from "./index.module.css"

const insertYouTubeAPI = (): void => {
  const tag = document.createElement("script")
  const firstScriptTag = document.querySelectorAll("script")[0]

  tag.src = "//www.youtube.com/iframe_api"
  firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag)
}

const getRutubeId = (url: string): string => {
  const m = url.match(/rutube\.ru\/video\/([\dA-Za-z]+)/)
  return m ? m[1] : ""
}

const getVimeoPoster = async (id: string | undefined): Promise<string> =>
  new Promise<string>((resolve) => {
    fetch(`https://vimeo.com/api/v2/video/${id}.json`)
      .then((response) => response.text())
      .then((responseText) => resolve(JSON.parse(responseText)[0].thumbnail_large))
      .catch((error) => {
        console.error(error)
      })
  })

const getYouTubeId = (url: string): string => {
  const newUrl = url.replace(/(>|<)/gi, "").split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/)

  if (newUrl[2] !== undefined) {
    return newUrl[2].split(/[^\w-]/i)[0]
  }

  return ""
}

const getVimeoId = (url: string): string => {
  const m = url.match(/^.+vimeo.com\/(.*\/)?([^#?]*)/)
  return m ? m[2] || m[1] : ""
}

const getId = (src: string): { type: "vimeo" | "youtube" | "rutube"; id: string } | undefined => {
  if (src.match(/vimeo/)) {
    return { type: "vimeo", id: getVimeoId(src) }
  }

  if (src.match(/youtu(\.)?be/)) {
    return { type: "youtube", id: getYouTubeId(src) }
  }

  if (src.match(/rutube\.ru/)) {
    return { type: "rutube", id: getRutubeId(src) }
  }

  return undefined
}

const getSource = (src: string): Promise<{ poster: string; source: string }> => {
  const { type, id } = { ...getId(src) }

  return new Promise<{ poster: string; source: string }>((resolve) => {
    switch (type) {
      case "vimeo":
        getVimeoPoster(id)
          .then((poster) =>
            resolve({
              poster,
              source: `https://player.vimeo.com/video/${id}?autoplay=1&color=fc0&byline=0`,
            }),
          )
          .catch((error) => {
            console.error(error)
          })
        break

      case "youtube":
        resolve({
          poster: `https://img.youtube.com/vi/${id}/sddefault.jpg`,
          source: `https://www.youtube.com/embed/${id}?&showinfo=0&autoplay=1&rel=0`,
        })
        break

      case "rutube":
        resolve({
          poster: "",
          source: `https://rutube.ru/play/embed/${id}?autoPlay=true`,
        })
        break
    }
  })
}

type VideoFrameProps = {
  src: string
  className?: string
  detailPicture?: string
}

export const VideoFrame: React.FC<VideoFrameProps> = ({ src, className, detailPicture }) => {
  const isVideo = src.match(/vimeo|youtu(\.)?be|rutube\.ru/)
  const [frameLoaded, loadFrame] = useState(false)
  const [poster, loadPoster] = useState("")
  const [source, setSource] = useState(src)
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })
  const clickHandler = useCallback(() => {
    loadFrame(true)
  }, [])
  useEffect(() => {
    const videoInfo = getId(src)
    if (videoInfo?.type === "youtube") {
      insertYouTubeAPI()
    }

    if (!isVideo) {
      loadFrame(true)
      return
    }

    getSource(src)
      // eslint-disable-next-line promise/always-return
      .then(({ poster, source }) => {
        setSource(source)
        loadPoster(poster)
      })
      .catch((error) => {
        console.error(error)
      })
  }, [isVideo, src])

  if (!frameLoaded && isVideo && !src.match(/rutube\.ru/)) {
    return (
      <div ref={ref} className={cn(className, styles.VideoFrame)} onClick={clickHandler}>
        {inView && <Poster src={detailPicture ?? poster} />}
      </div>
    )
  }

  return (
    <div className={cn(className, styles.VideoFrame)} data-js="active-iframe-container">
      <iframe frameBorder="0" src={source} allowFullScreen />
    </div>
  )
}

const Poster: React.FC<{ src: string }> = ({ src }) => {
  const [frameLoaded, loadFrame] = useState(false)

  useEffect(() => {
    const image = new Image()
    image.src = src
    image.addEventListener("load", () => loadFrame(true))
  }, [src])

  if (!frameLoaded) {
    return null
  }

  return <div className={styles.Poster} style={{ backgroundImage: `url(${src})` }} />
}
